import React, { useState, useEffect } from 'react';
import {
    Star,
    X,
    Truck,
    ShoppingBag,
    MessageSquare,
    Pill,
    ShieldCheck,
    Stethoscope,
    CheckCircle2,
    Sparkles,
    Check
} from 'lucide-react';
import { Button } from './Button';
import { api } from '../lib/axios';
import toast from 'react-hot-toast';
import { useTranslation } from "react-i18next";

interface OrderReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    orderData?: any;
    onSuccess: () => void;
}

const PHARMACY_TAG_OPTIONS = [
    'Genuine Medicine',
    'Cold-Chain Maintained',
    'Clear Dosage Labels',
    'Prompt Dispensing',
    'Helpful Pharmacist',
    'Sealed Packaging'
];

export const OrderReviewModal: React.FC<OrderReviewModalProps> = ({
    isOpen,
    onClose,
    orderId,
    orderData: initialOrderData,
    onSuccess
}) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState(5);
    const [deliveryRating, setDeliveryRating] = useState(5);
    const [comment, setComment] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderDetail, setOrderDetail] = useState<any>(initialOrderData || null);
    const [isLoadingOrder, setIsLoadingOrder] = useState<boolean>(!initialOrderData);

    useEffect(() => {
        if (initialOrderData) {
            setOrderDetail(initialOrderData);
            setIsLoadingOrder(false);
        }
    }, [initialOrderData]);

    useEffect(() => {
        if (isOpen && orderId) {
            if (initialOrderData && initialOrderData.id === orderId) {
                setOrderDetail(initialOrderData);
                setIsLoadingOrder(false);
                return;
            }

            setIsLoadingOrder(true);
            // Fetch order info using the owner-accessible endpoint
            api.get(`/ecommerce/order-details/${orderId}`)
                .then(res => {
                    if (res.data) {
                        setOrderDetail(res.data);
                    }
                })
                .catch(() => {
                    // Fallback to /owners/orders
                    return api.get('/owners/orders')
                        .then(res => {
                            const found = (res.data || []).find((o: any) => o.id === orderId);
                            if (found) setOrderDetail(found);
                        });
                })
                .catch((err) => {
                    console.error('Failed to fetch order details for review modal:', err);
                })
                .finally(() => {
                    setIsLoadingOrder(false);
                });
        }
    }, [isOpen, orderId, initialOrderData]);

    if (!isOpen) return null;

    if (isLoadingOrder && !orderDetail) {
        return (
            <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-md z-[200] flex items-center justify-center p-4">
                <div className="bg-white rounded-[32px] p-8 max-w-sm w-full shadow-2xl flex flex-col items-center justify-center text-center space-y-3">
                    <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs font-bold text-slate-600">Loading prescription details...</p>
                </div>
            </div>
        );
    }

    // Determine if this order is from a Veterinary Pharmacy / Prescription order
    const isPharmacy = 
        orderDetail?.orderType === 'PRESCRIPTION' ||
        orderDetail?.agent?.user?.role === 'VET_PHARMACY' ||
        orderDetail?.agent?.name?.toLowerCase().includes('pharmacy') ||
        orderDetail?.items?.some((it: any) => it.product?.category === 'MEDICINE' || it.product?.category === 'PHARMACY') ||
        (orderDetail?.shippingAddress && (
            orderDetail.shippingAddress.includes('Doctor:') || 
            orderDetail.shippingAddress.includes('Patient:') ||
            orderDetail.shippingAddress.includes('Rx:') ||
            orderDetail.shippingAddress.includes('Rx Items:')
        ));

    // Extract medicine and patient info
    let petName = '';
    let medicineName = '';
    let doctorName = '';
    let doctorAdvice = '';
    const pharmacyName = orderDetail?.agent?.businessName || orderDetail?.agent?.name || 'Veterinary Pharmacy Desk';

    if (orderDetail) {
        if (orderDetail.shippingAddress) {
            const parts = orderDetail.shippingAddress.split(' • ');
            parts.forEach((p: string) => {
                if (p.startsWith('Patient:')) petName = p.replace(/^Patient:\s*/, '').trim();
                else if (p.startsWith('Doctor:')) doctorName = p.replace(/^Doctor:\s*/, '').trim();
                else if (p.startsWith('Rx Items:') || p.startsWith('Rx:')) medicineName = p.replace(/^Rx( Items)?:\s*/, '').trim();
                else if (p.startsWith('Advice:') || p.startsWith('Directions:')) doctorAdvice = p.replace(/^(Advice|Directions):\s*/, '').trim();
            });
        }
        if (!medicineName && orderDetail.items && orderDetail.items.length > 0) {
            medicineName = orderDetail.items.map((it: any) => it.product?.title || 'Medicine').join(', ');
        }
    }

    const toggleTag = (tag: string) => {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/ecommerce/reviews', {
                orderId,
                rating,
                deliveryRating,
                comment,
                tags: selectedTags
            });
            toast.success(isPharmacy ? 'Thank you! Your pharmacy review was submitted.' : 'Thank you for your feedback!');
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to submit review');
        } finally {
            setIsSubmitting(false);
        }
    };

    const StarSelector = ({ value, onChange, label, sublabel, icon: Icon, colorClass, activeFill }: any) => (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg bg-slate-100 ${colorClass}`}>
                        <Icon size={16} />
                    </div>
                    <div>
                        <span className="text-xs font-black text-slate-800 tracking-tight block">{label}</span>
                        {sublabel && <span className="text-[10px] text-slate-400 font-medium">{sublabel}</span>}
                    </div>
                </div>
                <span className="text-sm font-black text-slate-800">{value}.0 / 5.0</span>
            </div>
            <div className="flex gap-2 pt-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => onChange(star)}
                        className="transition-transform hover:scale-110 active:scale-95 outline-none p-0.5"
                    >
                        <Star
                            size={28}
                            className={`${star <= value ? (activeFill || 'fill-amber-400 text-amber-400') : 'text-slate-200'}`}
                        />
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-100 max-h-[90vh] flex flex-col">
                
                {/* Header */}
                <div className={`p-6 border-b flex justify-between items-center ${
                    isPharmacy ? 'bg-gradient-to-r from-emerald-950 via-teal-950 to-slate-900 text-white border-emerald-900' : 'bg-slate-50/70 border-slate-100'
                }`}>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            {isPharmacy ? (
                                <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                                    <ShieldCheck size={11} /> Licensed Pharmacy Dispensing
                                </span>
                            ) : (
                                <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-slate-200 text-slate-600">
                                    Verified Order
                                </span>
                            )}
                        </div>
                        <h2 className={`text-xl font-black tracking-tight ${isPharmacy ? 'text-white' : 'text-slate-900'}`}>
                            {isPharmacy ? 'Rate Medicine Dispensing' : t('ui_rate_your_purch_8e4cdf', 'Rate Your Purchase')}
                        </h2>
                        <p className={`text-[11px] font-medium mt-0.5 ${isPharmacy ? 'text-emerald-200/80' : 'text-slate-500'}`}>
                            {isPharmacy ? pharmacyName : `${t('ui_order___d1228f', 'Order #')}${orderId.slice(0, 8).toUpperCase()}`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-full transition-colors ${
                            isPharmacy ? 'bg-white/10 hover:bg-white/20 text-white' : 'hover:bg-slate-100 text-slate-400'
                        }`}
                    >
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6 overflow-y-auto">
                    
                    {/* Clinical Prescription Info Card */}
                    {isPharmacy && (medicineName || petName) && (
                        <div className="bg-emerald-50/70 border border-emerald-100/90 rounded-2xl p-3.5 flex items-start gap-3">
                            <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-emerald-700/20">
                                <Pill size={16} />
                            </div>
                            <div className="space-y-0.5 flex-1 min-w-0">
                                <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">
                                    Dispensed Medication
                                </div>
                                <div className="text-xs font-black text-slate-900 truncate">
                                    {medicineName || 'Veterinary Medication Protocol'}
                                </div>
                                {petName && (
                                    <div className="text-[11px] text-slate-500 font-medium">
                                        Patient: <strong className="text-slate-700">{petName}</strong>
                                        {doctorName && <span> • Prescribed by {doctorName}</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Ratings */}
                    <div className="space-y-5 bg-slate-50/60 p-4 rounded-2xl border border-slate-100">
                        {isPharmacy ? (
                            <>
                                <StarSelector
                                    value={rating}
                                    onChange={setRating}
                                    label="Medicine Quality & Packaging"
                                    sublabel="Authentic medicine, tamper-evident seals & cold-chain secure"
                                    icon={Pill}
                                    colorClass="text-emerald-700"
                                    activeFill="fill-emerald-500 text-emerald-500"
                                />
                                <div className="h-px bg-slate-200/70" />
                                <StarSelector
                                    value={deliveryRating}
                                    onChange={setDeliveryRating}
                                    label="Pharmacist Care & Fulfillment Speed"
                                    sublabel="Prompt verification, clear dosage directions & clinical advice"
                                    icon={Stethoscope}
                                    colorClass="text-teal-700"
                                    activeFill="fill-teal-500 text-teal-500"
                                />
                            </>
                        ) : (
                            <>
                                <StarSelector
                                    value={rating}
                                    onChange={setRating}
                                    label={t('ui_product_quality_72371d', 'Product Quality')}
                                    sublabel="Quality, accuracy and condition of received items"
                                    icon={ShoppingBag}
                                    colorClass="text-brand-500"
                                    activeFill="fill-amber-400 text-amber-400"
                                />
                                <div className="h-px bg-slate-200/70" />
                                <StarSelector
                                    value={deliveryRating}
                                    onChange={setDeliveryRating}
                                    label={t('ui_delivery_servic_5cdd30', 'Delivery Service')}
                                    sublabel="Handling, packaging and delivery speed"
                                    icon={Truck}
                                    colorClass="text-blue-500"
                                    activeFill="fill-amber-400 text-amber-400"
                                />
                            </>
                        )}
                    </div>

                    {/* Quick Medical Tag Pills */}
                    {isPharmacy && (
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                                <Sparkles size={13} className="text-emerald-600" />
                                What went well with this dispensation?
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {PHARMACY_TAG_OPTIONS.map((tag) => {
                                    const isSelected = selectedTags.includes(tag);
                                    return (
                                        <button
                                            key={tag}
                                            type="button"
                                            onClick={() => toggleTag(tag)}
                                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                                                isSelected
                                                    ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-700/20'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {isSelected && <Check size={12} />}
                                            {tag}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Written Comment */}
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                            <MessageSquare size={13} />
                            {isPharmacy ? 'Clinical Feedback & Notes' : t('ui_comment__option_3b45c3', 'Comment (Optional)')}
                        </label>
                        <textarea
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 text-xs p-3.5 min-h-[90px] outline-none transition-all resize-none text-slate-800"
                            placeholder={
                                isPharmacy
                                    ? 'How was the medicine quality, cold-chain safety, dispensing speed, or pharmacist guidance?'
                                    : t('ui_tell_us_what_yo_6180b8', 'Tell us what you liked or how we can improve...')
                            }
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3.5 bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 transition-all text-xs"
                        >
                            {t('ui_later_61057a', 'Maybe Later')}
                        </button>
                        <Button
                            fullWidth
                            type="submit"
                            disabled={isSubmitting}
                            className={`flex-[2] text-white font-bold rounded-2xl shadow-lg border-transparent h-12 text-xs flex items-center justify-center gap-2 ${
                                isPharmacy
                                    ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-700/20'
                                    : 'bg-slate-900 hover:bg-slate-800 shadow-slate-900/20'
                            }`}
                        >
                            <CheckCircle2 size={16} />
                            {isSubmitting ? 'Submitting...' : isPharmacy ? 'Submit Pharmacy Review' : 'Submit Review'}
                        </Button>
                    </div>

                </form>
            </div>
        </div>
    );
};
